import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Checkbox } from '@els/els-react--checkbox';
import Button from '@Components/Button';
import { PopupRow } from '@Pages/RetroSynthesis/ProjectParametersDialog/ProjectParametersUtils';
import classnames from 'classnames';
import useHideTooltipsOnScroll from '@Components/Tooltip/useHideTooltipsOnScroll';
import HeaderInfoIcon from '../ProjectRoutes/components/HeaderInfoIcon';
import HeaderInfoTooltip from '../ProjectRoutes/components/HeaderInfoTooltip';
import { onProjectParametersSectionClick } from './anaytics';

import './ProjectParametersSection.theme.scss';

function ProjectParametersSection({
  className,
  label,
  editable,
  selected,
  editSate,
  onSelectedChanged,
  onEditSateChanged,
  tooltip,
  disabled,
  vendorsInfo,
  // eslint-disable-next-line react/prop-types
  children,
  infoTooltipAriaLabel,
  infoTooltipIconTitle,
  sectionType,
}) {
  const containerRef = useRef(null);
  useHideTooltipsOnScroll(containerRef);

  return (
    <div className={classnames(className, 'rs-project-parameters')} ref={containerRef}>
      <div className="rs-project-parameters__header">
        <PopupRow className="rs-info-column">
          {editable ? (
            <Checkbox
              className="checkbox_no-padding rs-project-parameters__enable"
              label={label}
              onChange={(event, isChecked) => {
                onSelectedChanged(isChecked);
              }}
              checked={selected}
              disabled={disabled}
            />
          ) : (
            <span className={classnames(disabled && 'els-disabled-state')}>{label}</span>
          )}
          <HeaderInfoIcon tooltipId={label} informationIconTitle={infoTooltipIconTitle} ariaLabel={infoTooltipAriaLabel} />
          <HeaderInfoTooltip id={label}>
            <div className="rs-tooltip-big__content">{tooltip}</div>
          </HeaderInfoTooltip>
        </PopupRow>
        {editable && (
          <Button
            buttonConfig={{
              type: 'link',
              size: 'sm',
              e2eName: 'params-section-toggle',
              ariaLabel: 'Toggle params',
            }}
            iconConfig={{ name: editSate ? 'chevron-up' : 'chevron-down' }}
            onClick={() => {
              onEditSateChanged(!editSate);
              onProjectParametersSectionClick(sectionType, false, !editSate);
            }}
            disabled={disabled}
          />
        )}
      </div>

      <div className={classnames(disabled && 'els-disabled-state')}>{children}</div>

      {editable && (
        <div className="rs-project-parameters__footer">
          {vendorsInfo && vendorsInfo.length && (
            <div className="rs-project-parameters__footer__vendors">
              Powered by
              {vendorsInfo.map((vendor) => {
                return (
                  <Button
                    key={vendor.name}
                    buttonConfig={{
                      type: 'link',
                      size: 'sm',
                      href: vendor.url,
                      e2eName: `vendor-logo-${vendor.name}`,
                      aTag: true,
                    }}
                    rel="external"
                    target="_blank">
                    <img
                      alt={vendor.name}
                      className={classnames('rs-project-parameters__footer__vendors-logo', `logo-${vendor.partner.toLowerCase()}`, {
                        'els-disabled-state': disabled,
                      })}
                      title={vendor.name}
                    />
                  </Button>
                );
              })}
            </div>
          )}
          {!editSate && (
            <Button
              buttonConfig={{
                label: 'Edit',
                type: 'link',
                size: 'sm',
                e2eName: 'params-section-edit',
              }}
              iconConfig={{ name: 'pencil' }}
              onClick={() => {
                onEditSateChanged(!editSate);
                onProjectParametersSectionClick(sectionType, true, !editSate);
              }}
              disabled={disabled}
            />
          )}
        </div>
      )}
    </div>
  );
}

ProjectParametersSection.defaultProps = {
  editSate: true,
  selected: true,
  editable: false,
  disabled: false,
  label: 'Section',
};

ProjectParametersSection.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string,
  onSelectedChanged: PropTypes.func,
  onEditSateChanged: PropTypes.func,
  selected: PropTypes.bool,
  editSate: PropTypes.bool,
  editable: PropTypes.bool,
  tooltip: PropTypes.any,
  disabled: PropTypes.bool,
  vendorsInfo: PropTypes.array,
  infoTooltipAriaLabel: PropTypes.string,
  infoTooltipIconTitle: PropTypes.string,
  sectionType: PropTypes.string,
};

export default ProjectParametersSection;
